import { computed } from 'vue';
import { GetAuditorDto, ProductDto } from '@studyportals/campaign-management-api-interface';
import { IAuditorData, IRefinedAuditorEntry, IRefinedVerifiedAuditorEntry, EntityKeys, IAuditorPricingCluster } from '@/models/auditor.interface';
import datePresenter from '@/presenters/date-presenter';
import store from '@/store';

export default class AuditorEntriesTableCode {
	public irrelevantPricingProductKeys = ['12', '17'];

	public get entityKeyStudy(): EntityKeys {
		return EntityKeys.STUDY;
	}

	public get entityKeyOrganisation(): EntityKeys {
		return EntityKeys.ORGANISATION;
	}

	public refinedAuditorEntries = computed((): IRefinedVerifiedAuditorEntry[] => {
		const refinedEntries: IRefinedAuditorEntry[] = store.getters.auditorEntries()
			.map((entry: GetAuditorDto) => {
				const refinedData = this.parseDataForEntry(entry);
				if (refinedData) {
					refinedData.pricing[EntityKeys.STUDY] = this.refinePricingCluster(refinedData.pricing[EntityKeys.STUDY]);
					refinedData.pricing[EntityKeys.ORGANISATION] = this.refinePricingCluster(refinedData.pricing[EntityKeys.ORGANISATION]);
				}
				return {
					...entry,
					refinedData
				};
			});
		return refinedEntries.filter(
			(entry: IRefinedAuditorEntry) => entry.refinedData !== undefined
		) as IRefinedVerifiedAuditorEntry[];
	});

	private allPricingProducts = computed((): ProductDto[] => {
		return store.getters.allPricingProducts();
	});

	public areGeoRemaindersAvailable(entry: IRefinedVerifiedAuditorEntry): boolean {
		return entry.refinedData._geo_remainders !== undefined && (
			entry.refinedData._geo_remainders[EntityKeys.STUDY] > 0 ||
			entry.refinedData._geo_remainders[EntityKeys.ORGANISATION] > 0
		);
	}

	public isGeoConfigurationRemoved(entry: IRefinedVerifiedAuditorEntry): boolean {
		return (
			entry.refinedData.geo.length === 0 &&
			Object.keys(entry.refinedData.pricing[EntityKeys.STUDY]).length === 0 &&
			Object.keys(entry.refinedData.pricing[EntityKeys.ORGANISATION]).length === 0
		);
	}

	public isPricingConfigurationAvailable(entry: IRefinedVerifiedAuditorEntry): boolean {
		return (
			Object.keys(entry.refinedData.pricing[EntityKeys.STUDY]).length > 0 ||
			Object.keys(entry.refinedData.pricing[EntityKeys.ORGANISATION]).length > 0
		);
	}

	public getPricingCountForEntryAndKey(entry: IRefinedVerifiedAuditorEntry, entityKey: EntityKeys): number {
		return Object.values(entry.refinedData.pricing[entityKey]).reduce(
			(sum, value) => sum + value,
			0
		);
	}

	public getPerformingUserNameForEntry(entry: IRefinedVerifiedAuditorEntry): string {
		if (!entry.isUserAction) {
			return 'Scheduler';
		}

		return entry.username;
	}

	public formatDateString(dateString: string): string {
		return datePresenter.formatDateString(dateString);
	}

	private refinePricingCluster(cluster: IAuditorPricingCluster): IAuditorPricingCluster {
		Object.keys(cluster).forEach((productKey) => {
			const value = cluster[productKey];
			delete cluster[productKey];

			if (this.irrelevantPricingProductKeys.includes(productKey)) {
				return;
			}

			const relevantPricingProduct = this.allPricingProducts.value.find(
				(product) => product.id.toString() === productKey
			);
			if (!relevantPricingProduct) {
				return;
			}

			cluster[relevantPricingProduct.description] = value;
		});

		return cluster;
	}

	private parseDataForEntry(entry: IRefinedAuditorEntry): IAuditorData | undefined {
		if (typeof entry.data !== 'string') {
			return;
		}

		return JSON.parse(entry.data) as IAuditorData;
	}
}
